import type { ReactNode } from 'react'
import { CheckIcon } from 'lucide-react'

import { cn } from 'cn'

/**
 * One numbered part of the onboarding desk. `done` marks a part the server has saved: the run
 * does not send it again, so its fields are locked.
 */
export function DeskSection({
  number,
  title,
  description,
  done = false,
  aside,
  children,
}: {
  number: number
  title: string
  description?: ReactNode
  done?: boolean
  aside?: ReactNode
  children: ReactNode
}) {
  const id = `desk-section-${number}`
  return (
    <section
      aria-labelledby={id}
      className="bg-card flex flex-col gap-4 rounded-lg border border-(--border) p-4 md:p-6"
    >
      <div className="flex flex-wrap items-start gap-3">
        <span
          aria-hidden="true"
          className={cn(
            'flex size-7 shrink-0 items-center justify-center rounded-full text-sm font-semibold tabular-nums',
            done ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' : 'bg-muted',
          )}
        >
          {done ? <CheckIcon className="size-4" /> : number}
        </span>
        <div className="flex min-w-0 flex-1 basis-48 flex-col gap-0.5">
          <h2 id={id} className="text-base leading-7 font-semibold">
            {title}
            {done ? <span className="sr-only"> (saved)</span> : null}
          </h2>
          {description === undefined ? null : (
            <p className="text-muted-foreground text-sm">{description}</p>
          )}
        </div>
        {aside}
      </div>
      {children}
    </section>
  )
}
